/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/start-templates/useQaStartFromTemplates.ts
 * FILE_ID: JOBHUB-QA-START-HOOK-001
 * PURPOSE: Load the library and create a QA inspection from chosen templates.
 */
import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { QaClassification } from '../../../qa/classification'
import type { QaApi } from '../../../qa/qaApi'
import type { QaTemplateDetail, QaTemplateSummary } from '../../../qa/qaTemplateTypes'
import { qaErrorMessage } from '../qaTabSelectors'
import { BLANK_TEMPLATE_DETAIL, BLANK_TEMPLATE_ID, groupByLocationType } from '../templates/qaTemplateCatalog'
import { loadQaTemplateLibrary } from '../templates/qaTemplateLibrary'
import { invalidateQaExecution } from '../useQaWorkspace'
import {
  buildFromTemplatesPayload,
  clearAll,
  countStart,
  selectAll,
  toggleLocation,
  toggleSection,
  toggleTemplate,
  type AreaNames,
  type StartSelection,
} from './qaStartTemplatesSelection'

export function useQaStartFromTemplates({
  api,
  jobId,
  classification,
  workspace,
  showBlank = false,
}: {
  api: Pick<QaApi, 'listTemplates' | 'getTemplate' | 'generateFromTemplates' | 'insertBlank'>
  jobId: number
  classification: QaClassification
  showBlank?: boolean
  workspace: {
    selectInspection: (inspectionId: string | null) => void
    openCreatedInspection?: (inspectionId: string) => void
    setAreaMode?: (mode: 'build' | 'run' | 'itp') => void
  }
}) {
  const queryClient = useQueryClient()
  const [panelOpen, setPanelOpen] = useState(false)
  const [templates, setTemplates] = useState<QaTemplateSummary[]>([])
  const [detailsById, setDetailsById] = useState<Record<string, QaTemplateDetail>>(
    showBlank ? { [BLANK_TEMPLATE_ID]: BLANK_TEMPLATE_DETAIL } : {},
  )
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selection, setSelection] = useState<StartSelection>({})
  const [areaNames, setAreaNames] = useState<AreaNames>({})
  const [name, setName] = useState('')
  const [loaded, setLoaded] = useState(false)

  const groups = groupByLocationType(templates, { includeBlank: showBlank })
  const counts = countStart(selection, groups)
  const detailsRef = useRef(detailsById)
  detailsRef.current = detailsById
  const rememberDetails = async (ids: string[]) => {
    const missing = ids.filter((id) => !detailsRef.current[id])
    if (missing.length === 0) return detailsRef.current
    const loaded = await Promise.all(missing.map((id) => api.getTemplate(id).catch(() => null)))
    const next = { ...detailsRef.current }
    for (const row of loaded) if (row) next[row.id] = row
    detailsRef.current = next
    setDetailsById(next)
    return next
  }

  const reload = async () => {
    setLoading(true)
    setError(null)
    try {
      const next = await loadQaTemplateLibrary(api, (rows) => setTemplates(rows))
      setTemplates(next.templates)
      setDetailsById(showBlank
        ? { [BLANK_TEMPLATE_ID]: BLANK_TEMPLATE_DETAIL, ...next.detailsById }
        : next.detailsById)
      setLoaded(true)
    } catch (err) {
      setError(qaErrorMessage(err, 'Could not load templates.'))
    } finally {
      setLoading(false)
    }
  }

  const openPanel = () => {
    setPanelOpen(true)
    if (!loaded && !loading) void reload()
  }

  const create = useMutation({
    mutationFn: async () => {
      const blankOn = showBlank && (selection[BLANK_TEMPLATE_ID]?.size ?? 0) > 0
      const rest = groups.filter((group) => group.location_type !== 'blank')
      const restSelected = rest.some((group) => group.templates.some((row) => (selection[row.id]?.size ?? 0) > 0))
      const blankArea = (areaNames.blank || '').trim() || 'Area'
      if (!blankOn) {
        return api.generateFromTemplates(
          buildFromTemplatesPayload(groups, selection, areaNames, detailsById, jobId, name, classification),
        )
      }
      if (!restSelected) {
        const seeded = await api.insertBlank({
          job_id: jobId,
          area_names: [blankArea],
          ...(name.trim() ? { inspection_name: name.trim() } : {}),
          only_if_empty: false,
        })
        return { id: seeded.inspection_id || '' }
      }
      const created = await api.generateFromTemplates(
        buildFromTemplatesPayload(rest, selection, areaNames, detailsById, jobId, name, classification),
      )
      await api.insertBlank({
        job_id: jobId,
        inspection_id: created.id,
        area_names: [blankArea],
        only_if_empty: false,
      })
      return created
    },
    onSuccess: (created) => {
      setSelection({})
      setAreaNames({})
      setName('')
      setPanelOpen(false)
      setError(null)
      ;(workspace.openCreatedInspection ?? workspace.selectInspection)(created.id)
      workspace.setAreaMode?.('build')
      invalidateQaExecution(queryClient, jobId)
    },
    onError: (err) => setError(qaErrorMessage(err, 'Could not create QA from templates.')),
  })

  return {
    panelOpen,
    openPanel,
    closePanel: () => setPanelOpen(false),
    groups,
    detailsById,
    loading,
    error,
    selection,
    areaNames,
    setAreaName: (key: string, value: string) => setAreaNames((current) => ({ ...current, [key]: value })),
    name,
    setName,
    counts,
    reload,
    selectAll: () => {
      const ids = groups.flatMap((group) => group.templates.map((row) => row.id))
      void rememberDetails(ids).then((details) => setSelection(selectAll(groups, details)))
    },
    clearAll: () => setSelection(clearAll()),
    toggleLocation: (group: (typeof groups)[number], on: boolean) => {
      void rememberDetails(group.templates.map((row) => row.id)).then((details) => {
        setSelection((current) => toggleLocation(current, group, details, on))
      })
    },
    toggleTemplate: (detail: QaTemplateDetail, on: boolean) => (
      setSelection((current) => toggleTemplate(current, detail, on))
    ),
    toggleSection: (templateId: string, sectionId: string) => (
      setSelection((current) => toggleSection(current, templateId, sectionId))
    ),
    create,
  }
}
